/**
 * DeleteDeckModal - Confirmation modal for deck deletion
 *
 * Shows a warning and requires explicit confirmation before deleting a deck.
 * Displays deck name and card count to help user confirm they're deleting the right deck.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { useCallback, useState } from 'react';
import { FaExclamationTriangle, FaTrash } from 'react-icons/fa';

import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import { useDeleteDeckMutation } from '@/features/decks/hooks/api';

// ============================================================================
// Types
// ============================================================================

export interface DeleteDeckModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Called when modal should close */
  onClose: () => void;
  /** Callback after successful deletion */
  onSuccess?: () => void;
  /** The deck being deleted */
  deck: {
    id: string;
    name: string;
    card_count: number;
  } | null;
}

// ============================================================================
// Component
// ============================================================================

export default function DeleteDeckModal({ isOpen, onClose, onSuccess, deck }: DeleteDeckModalProps): ReactElement {
  const [error, setError] = useState<string | null>(null);
  const deleteMutation = useDeleteDeckMutation();

  const isPending = deleteMutation.isPending;

  // Handle delete confirmation
  const handleDelete = useCallback(async () => {
    if (!deck) return;

    setError(null);

    try {
      await deleteMutation.mutateAsync(deck.id);
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Failed to delete deck:', err);
      setError('Failed to delete deck. Please try again.');
    }
  }, [deck, deleteMutation, onSuccess, onClose]);

  // Handle modal close
  const handleClose = useCallback(() => {
    if (!isPending) {
      setError(null);
      onClose();
    }
  }, [isPending, onClose]);

  if (!deck) return <></>;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Delete Deck?" size="sm">
      <div className="space-y-4">
        {/* Warning icon and message */}
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-500/10">
            <FaExclamationTriangle className="h-5 w-5 text-red-400" />
          </div>
          <div>
            <p className="text-zinc-200">
              This action <span className="font-semibold text-red-400">cannot be undone</span>.
            </p>
            <p className="mt-1 text-sm text-zinc-400">
              Are you sure you want to delete <span className="font-medium text-zinc-200">&quot;{deck.name}&quot;</span>?
            </p>
          </div>
        </div>

        {/* What will be deleted */}
        <div className="rounded-lg border border-zinc-700 bg-zinc-800/50 px-4 py-3">
          <p className="text-sm text-zinc-400">This will permanently remove:</p>
          <ul className="mt-2 space-y-1 text-sm text-zinc-300">
            <li className="flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-zinc-500" />
              The deck metadata
            </li>
            <li className="flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-zinc-500" />
              All {deck.card_count} cards in the deck
            </li>
          </ul>
        </div>

        {/* Error message */}
        {error && <div className="rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-400">{error}</div>}

        {/* Actions */}
        <div className="flex justify-end gap-3 border-t border-zinc-700 pt-4">
          <Button type="button" text="Cancel" variant="text" onClick={handleClose} disabled={isPending} />
          <button
            type="button"
            onClick={handleDelete}
            disabled={isPending}
            className={clsx(
              'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors',
              'bg-red-500/90 text-white hover:bg-red-500',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            <FaTrash className="h-3.5 w-3.5" />
            {isPending ? 'Deleting...' : 'Delete Deck'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
